import React from 'react';
import { styled } from '@mui/material';
import {
  numberToCurrencyString,
  numberToPercentageString,
} from 'further-ui/utils';
import { AllocationRule } from 'further-types/lp/allocations';
import TabsPanel from 'further-ui/components/TabsPanel';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { Column } from 'further-ui/components/Layout';
import AvatarWithName from 'components/new/AvatarWithName';

const ContentColumn = styled(Column)(({ theme }) => ({
  padding: theme.spacing(
    0,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
  ),
}));

type AdvanceSubscription = {
  actionId: string;
  entryId: string;
  name: string;
  totalAmountLcy: number;
  allocationRule: AllocationRule;
  originalInvestorBreakdown: Array<{
    commitmentId: string;
    investorName: string;
    totalCommitmentLcy: number;
    calledFundsLcy: number;
    allocationPercentage: number;
  }>;
  newInvestorBreakdown: Array<{
    commitmentId: string;
    investorName: string;
    totalCommitmentLcy: number;
    calledFundsLcy: number;
    allocationPercentage: number;
    excludeFromAllocation: boolean;
    callOverpaidOrUnderpaid: number;
  }>;
  numberOfEntries: number;
};

type AdvanceSubscriptionCardProps = {
  advanceSubscription: AdvanceSubscription;
  baseCurrency: string;
};

const AdvanceSubscriptionCard: React.FC<AdvanceSubscriptionCardProps> = ({
  advanceSubscription,
  baseCurrency,
}) => {
  const originalRows = advanceSubscription.originalInvestorBreakdown.map(
    (row, idx) => ({ id: `original-${idx}`, ...row }),
  );
  const newRows = advanceSubscription.newInvestorBreakdown.map((row, idx) => ({
    id: `new-${idx}`,
    ...row,
  }));

  type OriginalRow = (typeof originalRows)[number];
  type NewRow = (typeof newRows)[number];

  const sharedColumns = <T extends OriginalRow>(): Array<TableColumn<T>> => [
    {
      key: 'investorName',
      label: 'Investor',
      render: (row) => <AvatarWithName name={row.investorName} />,
    },
    {
      key: 'totalCommitmentLcy',
      label: 'Total Commitment',
      render: (row) =>
        numberToCurrencyString(row.totalCommitmentLcy, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
    {
      key: 'calledFundsLcy',
      label: 'Called funds (LCY)',
      render: (row) =>
        numberToCurrencyString(row.calledFundsLcy, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
    {
      key: 'allocationPercentage',
      label: 'Allocation % of called funds',
      render: (row) =>
        numberToPercentageString(row.allocationPercentage, {
          accuracy: 2,
          noMultiply: true,
        }),
    },
  ];

  const newColumns: Array<TableColumn<NewRow>> = [
    ...sharedColumns<NewRow>(),
    {
      key: 'callOverpaidOrUnderpaid',
      label: 'Call underpaid/(overpaid)',
      render: (row) =>
        numberToCurrencyString(row.callOverpaidOrUnderpaid, {
          fallback: '-',
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
  ];

  return (
    <TabsPanel
      defaultTab="after"
      tabs={[
        {
          key: 'after',
          label: 'New allocation',
          content: (
            <ContentColumn>
              <Table
                showTopBorder
                showHeaderBorder
                columns={newColumns}
                rows={newRows}
                scrollable={true}
              />
            </ContentColumn>
          ),
        },
        {
          key: 'before',
          label: 'Original allocation',
          content: (
            <ContentColumn>
              <Table
                showTopBorder
                showHeaderBorder
                columns={sharedColumns()}
                rows={originalRows}
                scrollable={true}
              />
            </ContentColumn>
          ),
        },
      ]}
    />
  );
};

export default AdvanceSubscriptionCard;
